import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  styled,
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const FormDesign = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedFormDesign, setSelectedFormDesign] = useState({ id: null, form_type: '' });
  const [formDesigns, setFormDesigns] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all form designs
  const fetchFormDesigns = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/form-designs/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      setFormDesigns(response.data);
      setFilteredData(response.data);
    } catch (err) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch form designs.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete a form design
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/form-designs/${selectedFormDesign.id}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const updatedFormDesigns = formDesigns.filter((form) => form.id !== selectedFormDesign.id);
      setFormDesigns(updatedFormDesigns);
      setFilteredData(updatedFormDesigns);
      setShowModal(false);
      setSnackbarMessage('Form design deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete form design.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  const handleAddFormDesign = () => {
    navigate('/add-form-design');
  };

  const handleEdit = (id) => {
    navigate(`/edit-form-design/${id}`);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
    } else {
      fetchFormDesigns();
    }
  }, [navigate]);

  useEffect(() => {
    const filtered = formDesigns.filter((form) =>
      form.form_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, formDesigns]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getFormTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'enquiry':
        return 'primary';
      case 'admission':
        return 'success';
      case 'registration':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by form type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddFormDesign}
            sx={{
              ml: 2,
              width: '185px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD FORM DESIGN
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px', backgroundColor: theme.palette.background.paper }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Form Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Number of Fields</TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      backgroundColor: theme.palette.mode === 'dark' ? '#064b7a' : theme.palette.primary.dark,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((form, index) => (
                    <TableRow
                      key={form.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                       {form.form_type.toUpperCase()}
                      </TableCell>

                      <TableCell>{form.layout_json?.fields?.length || 0}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(form.id)}
                          sx={{ color: theme.palette.primary.light }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedFormDesign({
                              id: form.id,
                              form_type: form.form_type
                            });
                            setShowModal(true);
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={4} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <KeyboardDoubleArrowLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <KeyboardDoubleArrowRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Modal open={showModal} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete the {selectedFormDesign.form_type} form design?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Form Design Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

<CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Add Form Design */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to add a new form design:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Click the <strong>"ADD FORM DESIGN"</strong> button to navigate to the form creation page.
    </li>
    <li>
      Select the <strong>form type</strong> from the available options: <strong>Enquiry</strong>, <strong>Admission</strong>, or <strong>Registration</strong>.
    </li>
    <li>
      Add fields dynamically by specifying:
      <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
        <li><strong>Label</strong> (the display name of the field)</li>
        <li><strong>Name</strong> (the internal identifier for the field)</li>
        <li><strong>Type</strong> (e.g., text, dropdown, checkbox)</li>
        <li><strong>Required</strong> (toggle to mark the field as mandatory)</li>
      </ul>
    </li>
    <li>
      Once all fields are added, click <strong>"SAVE"</strong> to create the form design. A success notification will confirm the creation.
    </li>
  </ul>

  {/* Section 2: Edit Form Design */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to edit a form design:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Locate the form design you want to edit in the list.
    </li>
    <li>
      Click the <strong>Edit</strong> icon (pencil) in the Action column to open the form editor.
    </li>
    <li>
      Modify the form fields as needed:
      <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
        <li>Update field labels, names, types, or required status.</li>
        <li>Add or remove fields using the dynamic field controls.</li>
      </ul>
    </li>
    <li>
      Click <strong>"UPDATE"</strong> to save your changes. A success notification will confirm the update.
    </li>
  </ul>

  {/* Section 3: Delete Form Design */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete a form design:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Find the form design you want to delete in the list.
    </li>
    <li>
      Click the <strong>Delete</strong> icon (trash) in the Action column.
    </li>
    <li>
      Confirm the deletion in the confirmation dialog. A success notification will appear once the form design is deleted.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted form designs cannot be recovered. Proceed with caution.
  </Typography>
</CustomTableScrollbar>


        </Box>
      </Modal>
    </Container>
  );
};

export default FormDesign;